import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Send, Mail, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Compositeur de relances : écrire / sélectionner / envoyer Email ou SMS
export function RelanceComposer({ quote, H, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [channel, setChannel] = useState('email');
  const [tplId, setTplId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingLink, setLoadingLink] = useState(false);
  const [payLinks, setPayLinks] = useState({});
  const [info, setInfo] = useState({
    client_email: quote.client_email, client_telephone: quote.client_telephone,
  });

  useEffect(() => {
    fetch(`${BACKEND}/api/devis/${quote.id}/relance-templates`, { headers: H })
      .then(r => r.json())
      .then(d => {
        setTemplates(d.templates || []);
        setInfo({ client_email: d.client_email, client_telephone: d.client_telephone });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.id]);

  const hasPhone = !!info.client_telephone;

  async function fetchPayLink(type) {
    if (payLinks[type]) return payLinks[type];
    setLoadingLink(true);
    try {
      const r = await fetch(`${BACKEND}/api/devis/${quote.id}/payment-link?type=${type}`, { headers: H });
      const d = await r.json();
      if (d.success && d.url) {
        setPayLinks(p => ({ ...p, [type]: d.url }));
        return d.url;
      }
      toast.warning(d.message || 'Lien de paiement indisponible');
    } catch { toast.error('Erreur lors de la génération du lien'); }
    finally { setLoadingLink(false); }
    return null;
  }

  async function applyTemplate(id, ch) {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    let subj = t.subject || '';
    let bdy = ch === 'sms' ? (t.sms_body || '') : (t.email_body || '');
    if (t.payment_type && bdy.includes('[LIEN DE PAIEMENT]')) {
      const url = await fetchPayLink(t.payment_type);
      bdy = bdy.replace('[LIEN DE PAIEMENT]', url || '[lien indisponible — ajoutez des articles au devis]');
    }
    setSubject(subj);
    setBody(bdy);
  }
  function selectTemplate(id) { setTplId(id); applyTemplate(id, channel); }
  function switchChannel(ch) { setChannel(ch); if (tplId) applyTemplate(tplId, ch); }

  async function send() {
    if (!body.trim()) { toast.error('Le message est vide'); return; }
    if (channel === 'sms' && !hasPhone) { toast.error('Aucun numéro de téléphone pour ce client'); return; }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/relance-message`, {
        method: 'POST', headers: H,
        body: JSON.stringify({ channel, subject, body }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      if (d.simulated) toast.warning(d.message);
      else if (d.success) toast.success(channel === 'sms' ? 'SMS envoyé !' : 'Email envoyé !');
      else toast.error(d.message || 'Échec de l\'envoi');
      onSent && onSent();
    } catch (e) { toast.error(e.message || 'Erreur'); }
    setSending(false);
  }

  const tabBtn = (active) => ({
    flex: 1, padding: '7px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    borderRadius: 8, border: '1px solid', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 6,
    background: active ? 'rgba(93,188,191,0.18)' : 'transparent',
    color: active ? 'var(--admin-teal)' : 'var(--admin-muted)',
    borderColor: active ? 'rgba(93,188,191,0.4)' : 'var(--admin-border)',
  });

  return (
    <div className="p-4 rounded-xl" data-testid="relance-composer"
      style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
      <p className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{ color: 'var(--admin-muted)' }}>
        <Send size={12} /> RELANCES & MESSAGES
      </p>

      {/* Canal Email / SMS */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button data-testid="relance-channel-email" onClick={() => switchChannel('email')} style={tabBtn(channel === 'email')}>
          <Mail size={13} /> Email
        </button>
        <button data-testid="relance-channel-sms" onClick={() => switchChannel('sms')}
          disabled={!hasPhone} title={!hasPhone ? 'Aucun numéro de téléphone' : ''}
          style={{ ...tabBtn(channel === 'sms'), opacity: hasPhone ? 1 : 0.4, cursor: hasPhone ? 'pointer' : 'not-allowed' }}>
          <MessageSquare size={13} /> SMS
        </button>
      </div>

      {/* Destinataire */}
      <p className="text-xs mb-2" style={{ color: 'var(--admin-faint)' }}>
        À : <span style={{ color: 'var(--admin-text)' }}>
          {channel === 'sms' ? (info.client_telephone || '— aucun numéro —') : (info.client_email || '— aucun email —')}
        </span>
      </p>

      {/* Sélecteur de modèle pré-écrit */}
      <label className="text-xs block mb-1" style={{ color: 'var(--admin-faint)' }}>Modèle pré-écrit</label>
      <select data-testid="relance-template-select" value={tplId}
        onChange={e => selectTemplate(e.target.value)}
        className="w-full mb-3 px-3 py-2 rounded-lg text-sm"
        style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }}>
        <option value="">— Choisir un modèle —</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>

      {/* Sujet (email uniquement) */}
      {channel === 'email' && (
        <>
          <label className="text-xs block mb-1" style={{ color: 'var(--admin-faint)' }}>Objet</label>
          <input data-testid="relance-subject-input" value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Objet de l'email"
            className="w-full mb-3 px-3 py-2 rounded-lg text-sm"
            style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
        </>
      )}

      {/* Corps du message — éditable */}
      <label className="text-xs block mb-1 flex items-center gap-1.5" style={{ color: 'var(--admin-faint)' }}>
        Message
        {loadingLink && <span style={{ color: 'var(--admin-teal)' }}>· génération du lien de paiement…</span>}
      </label>
      <textarea data-testid="relance-body-textarea" value={body} onChange={e => setBody(e.target.value)}
        rows={channel === 'sms' ? 4 : 8}
        placeholder={channel === 'sms' ? 'Votre SMS…' : 'Votre message…'}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none', resize: 'vertical', lineHeight: 1.5 }} />

      {channel === 'sms' && (
        <p className="text-xs mt-1" style={{ color: body.length > 160 ? '#F59E0B' : 'var(--admin-faint)' }}>
          {body.length} caractères {body.length > 160 ? `· ${Math.ceil(body.length / 153)} SMS` : ''}
        </p>
      )}

      <Button onClick={send} disabled={sending || !body.trim() || (channel === 'sms' && !hasPhone)}
        data-testid="relance-send-button" className="w-full mt-3"
        style={{ background: 'rgba(93,188,191,0.18)', color: 'var(--admin-teal)', border: '1px solid rgba(93,188,191,0.35)' }}>
        <Send size={14} className="mr-2" />
        {sending ? 'Envoi…' : channel === 'sms' ? 'Envoyer le SMS' : 'Envoyer l\'email'}
      </Button>
    </div>
  );
}
